import React, { useState, useEffect } from 'react';
import { Calendar, Grid, Layout, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import ClientDayCalendar from './ClientDayCalendar';
import ClientWeekCalendar from './ClientWeekCalendar';
import ClientDashboardDayView from './ClientDashboardDayView';
import ClientDashboardWeekView from './ClientDashboardWeekView';
import ClientMonthCalendar from './ClientMonthCalendar';
import { useTheme } from '../contexts/ThemeContext';
import './ClientDashboardMobile.css';

function ClientCalendarView({ db, client }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeView, setActiveView] = useState('month'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const { theme } = useTheme();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && activeView === 'month') {
      setActiveView('week');
    }
  }, [isMobile, activeView]);

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (time) => {
    return time.substring(0, 5); // Format HH:MM
  };

  const formatDate = (date, format = 'short') => {
    const dateObj = new Date(date);
    if (format === 'eee. d MMMM') {
      return dateObj.toLocaleDateString('fr-FR', {
        weekday: 'short',
        day: 'numeric',
        month: 'long'
      });
    }
    return dateObj.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  const renderView = () => {
    switch (activeView) {
      case 'day':
        return isMobile ? (
          <ClientDashboardDayView
            db={db}
            client={client}
            currentDate={currentDate}
            formatDate={formatDate}
            formatTime={formatTime}
          />
        ) : (
          <ClientDayCalendar
            db={db}
            client={client}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
          />
        );
      case 'week':
        return isMobile ? (
          <ClientDashboardWeekView
            db={db}
            client={client}
            currentDate={currentDate}
            formatDate={formatDate}
            formatTime={formatTime}
          />
        ) : (
          <ClientWeekCalendar
            db={db}
            client={client}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
          />
        );
      case 'month':
        return (
          <ClientMonthCalendar
            db={db}
            client={client}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
          />
        );
      default:
        return (
          <ClientMonthCalendar
            db={db}
            client={client}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
          />
        );
    }
  };

  // Helper pour le numéro de semaine
  const getWeekNumber = (d) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  const getWeekRange = (date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return {
      start: start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      end: end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      weekNumber: getWeekNumber(start)
    };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 client-dashboard-calendar">
      {/* Navigation Tabs & Header - Optimized for Mobile */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">

        {/* Title & Week Info */}
        <div className="flex flex-col">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
            <Calendar className="mr-2 text-blue-600 dark:text-blue-400" size={24} />
            Planning
          </h2>
          {/* Week Info Display - Only visible in Week View */}
          {activeView === 'week' && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-1">
              Semaine {getWeekRange(currentDate).weekNumber} • {getWeekRange(currentDate).start} - {getWeekRange(currentDate).end}
            </div>
          )}
          {/* Day Info Display - Only visible in Day View */}
          {activeView === 'day' && (
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-1">
              {formatDate(currentDate, 'eee. d MMMM')}
            </div>
          )}
        </div>

        {/* Controls Container - Wraps on small screens */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">

          {/* View Switcher (Day/Week) - Full width on mobile for easier tap */}
          <div className="flex bg-gray-100 dark:bg-gray-700 p-1 rounded-lg w-full sm:w-auto">
            <button
              onClick={() => setActiveView('day')}
              className={`flex-1 sm:flex-none flex justify-center items-center px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeView === 'day'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Jour
            </button>
            <button
              onClick={() => setActiveView('week')}
              className={`flex-1 sm:flex-none flex justify-center items-center px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeView === 'week'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Semaine
            </button>
            {!isMobile && (
              <button
                onClick={() => setActiveView('month')}
                className={`flex justify-center items-center px-4 py-2 rounded-md font-medium text-sm transition-colors ${activeView === 'month'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300'
                  }`}
              >
                Mois
              </button>
            )}
          </div>

          {/* Navigation Buttons (< Today >) */}
          <div className="flex items-center justify-between w-full sm:w-auto gap-2">
            <button
              onClick={() => {
                if (activeView === 'day') navigateDay(-1);
                else if (activeView === 'week') navigateWeek(-1);
                else if (activeView === 'month') navigateMonth(-1);
              }}
              className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex-1 sm:flex-none flex justify-center"
            >
              <ChevronLeft size={20} />
            </button>

            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800 font-medium flex-grow sm:flex-grow-0 text-center"
            >
              Aujourd'hui
            </button>

            <button
              onClick={() => {
                if (activeView === 'day') navigateDay(1);
                else if (activeView === 'week') navigateWeek(1);
                else if (activeView === 'month') navigateMonth(1);
              }}
              className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex-1 sm:flex-none flex justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* View Content */}
      <div className="client-dashboard-calendar-content">
        {renderView()}
      </div>
    </div>
  );
}

export default ClientCalendarView;
